import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck } from 'react-icons/fa';
import dayjs from 'dayjs';
import ProductFormModal from './ProductFormModal';
import ProductViewModal from './ProductViewModal';
import Sidebar from './Sidebar';
import {  useNavigate } from 'react-router-dom';

const API_URL = '/api';

const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate(); 

  useEffect(() => {
    fetchProducts();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/products`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedProducts = [...products].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'CreatedAt') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'ProductCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setProducts(sortedProducts);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setShowView(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product.ProductCode);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (product) => {
    if (!window.confirm('Are you sure you want to deactivate this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/products/${product.ProductCode}/deactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate product');
      }
      
      fetchProducts();
    } catch (error) {
      console.error('Error deactivating product:', error);
      alert(error.message || 'An error occurred while deactivating the product');
    }
  };

  const handleReactivate = async (product) => {
    if (!window.confirm('Are you sure you want to reactivate this product?')) return;
    
    try {
      const response = await fetch(`${API_URL}/products/${product.ProductCode}/reactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate product');
      }
      
      fetchProducts();
    } catch (error) {
      console.error('Error reactivating product:', error);
      alert(error.message || 'An error occurred while reactivating the product');
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      'ProductCode', 'ProductName', 'CategoryName', 'UOMName', 'Status', 'CreatedAt'
    ];
    const rows = products.map(p => [
      p.ProductCode, p.ProductName, p.CategoryName || '-', p.UOMName || '-', p.Status, 
      dayjs(p.CreatedAt).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    if (formLoading) return; // Prevent double submission
    
    setFormLoading(true);
    try {
      if (isEdit && selectedProduct) {
        const response = await fetch(`${API_URL}/products/${selectedProduct}`, {
          method: 'PUT',
          body: formData,
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update product');
        }
      } else {
        const response = await fetch(`${API_URL}/products`, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.message && errorData.message.includes('duplicate key')) {
            // Close the form and reopen it to get a fresh product code
            setShowForm(false);
            setTimeout(() => {
              setShowForm(true);
              alert('Duplicate product code detected. The form has been refreshed with a new product code. Please try again.');
            }, 100);
            return;
          }
          throw new Error(errorData.message || 'Failed to create product');
        }
      }
      setShowForm(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert(error.message || 'An error occurred while saving the product');
    } finally {
      setFormLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    {/* Sidebar */}
    <Sidebar onLogout={handleLogout} />

    {/* Main Content */}
    <main className="flex-1 p-2 sm:p-8 overflow-hidden ml-0 sm:ml-16 transition-all duration-300">
      <div className="flex flex-col h-full">

        {/* Header */}
<div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center mb-4 sm:mb-6 gap-3">
  <h1 className="text-xl sm:text-3xl font-bold text-[#2b6777]">
    Product Master
  </h1>
  {/* <button
    className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-3 sm:px-4 py-2 rounded-lg shadow transition text-sm sm:text-base"
    onClick={handleCreate}
  >
    <FaPlus className="text-sm sm:text-base" /> Create New Product
  </button> */}
</div>


        {/* Table Wrapper */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto scroll-smooth">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[700px] bg-white rounded-2xl shadow-lg border-separate border-spacing-0">
                <thead>
                  <tr>
                    {[
                      { key: "ProductCode", label: "Product Code" },
                      { key: "ProductName", label: "Product Name" },
                      { key: "CategoryName", label: "Category" },
                      { key: "UOMName", label: "UOM" },
                      { key: "Status", label: "Status" },
                      { key: "CreatedAt", label: "Created At" },
                    ].map((col, i) => (
                      <th
                        key={col.key}
                        className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] 
                          px-2 sm:px-6 py-2 sm:py-4 text-center border-r border-white 
                          text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] 
                          transition-all duration-300 ${isScrolled ? "shadow-lg" : ""} 
                          ${i === 0 ? "rounded-tl-2xl" : ""}`}
                        onClick={() => handleSort(col.key)}
                      >
                        <div className="flex items-center justify-center">
                          {col.label} {getSortIcon(col.key)}
                        </div>
                      </th>
                    ))}
                    <th
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] 
                        px-2 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm 
                        font-bold rounded-tr-2xl transition-all duration-300 
                        ${isScrolled ? "shadow-lg" : ""}`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-6 sm:py-10 text-[#AAD7D9] font-semibold text-sm sm:text-base"
                      >
                        Loading products...
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-6 sm:py-10 text-[#AAD7D9] font-semibold text-sm sm:text-base"
                      >
                        No products found.
                      </td>
                    </tr>
                  ) : (
                    products.map((product, idx) => (
                      <tr
                        key={product.ProductCode}
                        className={`transition-all duration-300 group ${
                          product.Status === "Active"
                            ? "hover:bg-[#eaf6f6] hover:shadow-md"
                            : "opacity-60"
                        } border-b last:border-b-0 transform hover:scale-[1.01]`}
                        style={{
                          boxShadow:
                            idx === products.length - 1
                              ? "0 8px 24px 0 rgba(44,103,119,0.04)"
                              : undefined,
                        }}
                      >
                        <td className="px-2 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                          <p
                            className="cursor-pointer transition-all duration-200 hover:scale-105 text-blue-600 border-b border-blue-600 w-fit"
                            onClick={() => handleView(product)}
                          >
                            {product.ProductCode}
                          </p>
                        </td>
                        <td className="px-2 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm font-semibold whitespace-nowrap">
                          {product.ProductName}
                        </td>
                        <td className="px-2 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm whitespace-nowrap">
                          {product.CategoryName || "-"}
                        </td>
                        <td className="px-2 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm whitespace-nowrap">
                          {product.UOMName || "-"}
                        </td>
                        <td className="px-2 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm">
                          <span
                            className={`inline-block px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-sm transition-all duration-200 ${
                              product.Status === "Active"
                                ? "bg-[#52ab98] text-white"
                                : "bg-gray-300 text-gray-500"
                            }`}
                          >
                            {product.Status}
                          </span>
                        </td>
                        <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-700">
                          {product.CreatedAt ? (
                            <>
                              {dayjs(product.CreatedAt).format("YYYY-MM-DD")}
                              <br />
                              <span className="text-[10px] sm:text-xs text-gray-400">
                                {dayjs(product.CreatedAt).format("HH:mm")}
                              </span>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-2 sm:px-6 py-2 sm:py-4 flex gap-2 justify-center items-center">
                          <button
                            title="View"
                            className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110"
                            onClick={() => handleView(product)}
                          >
                            <FaEye className="text-xs sm:text-base" />
                          </button>
                          <button
                            title="Edit"
                            className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110"
                            onClick={() => handleEdit(product)}
                          >
                            <FaEdit className="text-xs sm:text-base" />
                          </button>
                          {product.Status === "Active" ? (
                            <button
                              title="Deactivate"
                              className="p-1 sm:p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110"
                              onClick={() => handleDeactivate(product)}
                            >
                              <FaBan className="text-xs sm:text-base" />
                            </button>
                          ) : (
                            <button
                              title="Reactivate"
                              className="p-1 sm:p-2 rounded-full hover:bg-green-100 text-green-500 transition-all duration-200 hover:scale-110"
                              onClick={() => handleReactivate(product)}
                            >
                              <FaCheck className="text-xs sm:text-base" />
                            </button>
                          )}
                          <button
                            title="Download CSV"
                            className="p-1 sm:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110"
                            onClick={handleDownloadCSV}
                          >
                            <FaDownload className="text-xs sm:text-base" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modals */}
        <ProductFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedProduct}
          isEdit={isEdit}
          loading={formLoading}
        />
        <ProductViewModal
          open={showView}
          onClose={() => setShowView(false)}
          product={selectedProduct}
        />
      </div>
    </main>
  </div>
);

};

export default ProductMaster; 